import { AIDA_CHARACTER_LIMIT, AIDA_WARNING_LIMIT, AidaService, CHAT_MENU_CONTEXT, LlmChatMenuController, LlmChatService, LlmForkService } from '@adaptiva/ai/common';
import { Alert } from '@adaptiva/core/alerts';
import { EChatMessageSource } from '@adaptiva/core/chat';
import { injectUnique } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { AlertPanelComponent } from '@adaptiva/widgets/alert-panel';
import { ChatComponent } from '@adaptiva/widgets/chat';
import { SearchNavigationComponent } from '@adaptiva/widgets/search-navigation';
import { ChangeDetectionStrategy, Component, inject, Injector, Signal, signal, viewChild, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, skip, takeUntil } from 'rxjs';
import { BaseLlmTabComponent } from '../base-llm-tab';
import { LlmChatFooter } from '../llm-chat-footer';

@Component({
	selector: 'ada-llm-chat',
	templateUrl: './llm-chat.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlertPanelComponent,
		ChatComponent,
		LlmChatFooter,
		SearchNavigationComponent,
	],
	host: {
		'[class.ada-display-block]': 'true',
		'[class.ada-full-size]': 'true',
	},
})
export class LlmChatComponent extends BaseLlmTabComponent {

	public readonly characterLimit: WritableSignal<int> = inject(AIDA_CHARACTER_LIMIT);

	public readonly warningThreshold: WritableSignal<int> = inject(AIDA_WARNING_LIMIT);

	protected readonly aida: AidaService = inject(AidaService);

	protected readonly allowSend: WritableSignal<boolean> = signal(true);

	protected readonly chatComponent: Signal<ChatComponent> = viewChild(ChatComponent);

	protected readonly chatService: LlmChatService = inject(LlmChatService);

	protected readonly errorAlertType: Alert.Type = Alert.Type.ERROR;

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly forkService: LlmForkService = inject(LlmForkService);

	protected readonly injector: Injector = inject(Injector);

	protected readonly isResponding: WritableSignal<boolean> = signal(false);

	protected readonly responseController: LlmChatMenuController = injectUnique(
		LlmChatMenuController,
		[{ provide: CHAT_MENU_CONTEXT, useValue: EChatMessageSource.RESPONSE }]
	);

	protected readonly queryController: LlmChatMenuController = injectUnique(
		LlmChatMenuController,
		[{ provide: CHAT_MENU_CONTEXT, useValue: EChatMessageSource.QUERY }]
	);

	protected search(_message: string): void {
		this.chatService.searchResults.set(this.chatService.find(_message));
	}

	protected submit(_message: string): void {
		if (this.isResponding())
			return;

		this.isResponding.set(true);
		this.chatService.suggestedPrompts.set([]);
		this.chatService.sendMessage(_message).pipe(
			this.takeOneUntilDestroyed(),
			takeUntil(
				toObservable(
					this.aida.conversationId, { injector: this.injector }
				).pipe(skip(1))
			),
			finalize(() => this.isResponding.set(false))
		).subscribe();
	}

}
